// engines/progress-manager.js — per-item play tracking.
//
// Port of `Assets/_App/Engines/ProgressManager.cs`. The Unity version persists
// to `Application.persistentDataPath/progress.json`; the web version persists
// via `data/progress-store.js` (IndexedDB).
//
// Architecture rule: this is a pure state machine over the `progress-store`
// data layer. No DOM imports.

import * as store from "../data/progress-store.js";

/** @typedef {import("../types").PlayRecord} PlayRecord */

/**
 * Threshold beyond which a session counts as "a play". Mirrors
 * COMPLETION_THRESHOLD = 0.85f from the Unity port.
 */
export const COMPLETION_THRESHOLD = 0.85;

/**
 * The screen calls this after `playbackEnded` (or on natural-end inferred
 * from `currentTimeSec / durationSec ≥ threshold`) to bump play count.
 *
 * @param {string} itemId
 * @returns {Promise<PlayRecord>}
 */
export async function recordPlay(itemId) {
  return store.recordPlay(itemId);
}

/**
 * Persist the current playback position so the user can resume.
 *
 * @param {string} itemId
 * @param {number} timeSec
 */
export async function savePlayhead(itemId, timeSec) {
  await store.savePlayhead(itemId, timeSec);
}

/**
 * Whether a given item has reached its required play count for the
 * list-context it is rendered in. `requiredPlays` is per-list, not per-item —
 * mirrors the rule from CLAUDE.md "Common Pitfalls" #7.
 *
 * @param {string} itemId
 * @param {number} requiredPlays
 * @returns {Promise<boolean>}
 */
export async function isComplete(itemId, requiredPlays) {
  const r = await store.getPlayRecord(itemId);
  if (!r) return false;
  if (r.completed) return true;
  return r.playCount >= requiredPlays;
}

/**
 * Fractional progress 0..1 toward `requiredPlays`. Useful for the
 * `<rm-progress-bubble>` widget.
 *
 * @param {string} itemId
 * @param {number} requiredPlays
 * @returns {Promise<number>}
 */
export async function getProgress(itemId, requiredPlays) {
  const r = await store.getPlayRecord(itemId);
  if (!r || requiredPlays <= 0) return 0;
  return Math.max(0, Math.min(1, r.playCount / requiredPlays));
}

/**
 * Heuristic for "should this session count as a play?". Mirrors
 * ProgressManager.ShouldCountPlay() from the Unity port.
 *
 * @param {number} currentTimeSec
 * @param {number} durationSec
 * @returns {boolean}
 */
export function shouldCountPlay(currentTimeSec, durationSec) {
  if (durationSec <= 0) return false;
  return currentTimeSec / durationSec >= COMPLETION_THRESHOLD;
}

/** @param {string} itemId */
export function getPlayhead(itemId) {
  return store.getPlayRecord(itemId).then((r) => r?.playheadSec ?? 0);
}

/** @param {string} itemId */
export function getPlayCount(itemId) {
  return store.getPlayRecord(itemId).then((r) => r?.playCount ?? 0);
}
